import React from 'react'
import { DataTableCount, DataTableStatus, DataTableText } from '../../../../shared/components/DataTable'
import { LinkedRecordsCount, LinkedRecordsExpansion } from '../../../../shared/components/LinkedRecordsPanel'
import { getDateLabel, toDisplayValue, toListLabel, toYesNo } from '../../../../shared/utils/displayValue'
import { ActivityStatusDetails } from '../../../../shared/hooks/useActivityStatuses/useActivityStatuses.types'
import { getBudgetLabel, getCompletionDateLabel } from '../ActivityTable.constants'
import { ActivityColumnId, ActivityExpandedPanel, ActivityRow } from '../ActivityTable.types'

type GetStatusDetails = (status: string) => ActivityStatusDetails

const text = (value?: string | null, maxLines?: number) => (
    <DataTableText text={toDisplayValue(value)} maxLines={maxLines} />
)

export const renderActivityCell =
    (getStatusDetails: GetStatusDetails, linkedRecordsExpansion?: LinkedRecordsExpansion<ActivityExpandedPanel>) =>
    (activity: ActivityRow, columnId: ActivityColumnId) => {
        const linkedRecordsCount = (panel: ActivityExpandedPanel, count: number) => (
            <LinkedRecordsCount
                count={count}
                ownerId={activity._id}
                ownerName={activity.title}
                panel={panel}
                expansion={linkedRecordsExpansion}
            />
        )

        switch (columnId) {
            case 'title':
                return (
                    <DataTableText
                        text={toDisplayValue(activity.title)}
                        variant="16x600"
                        color="figma.grayscale.textHier1"
                        maxLines={3}
                    />
                )
            case 'titleLong':
                return text(activity.titleLong, 3)
            case 'activityTimeline':
                return text(activity.activityTimelineLabel)
            case 'status': {
                const { icon, name } = getStatusDetails(activity.status)
                return <DataTableStatus icon={icon} label={name} />
            }
            case 'completionDate':
                return <DataTableText text={getCompletionDateLabel(activity)} />
            case 'category':
                return text(activity.category)
            case 'function':
                return text(activity.function)
            case 'type':
                return text(activity.type)
            case 'dataSource':
                return text(activity.dataSource)
            case 'population':
                return text(activity.population)
            case 'sponsorType':
                return text(activity.sponsorType)
            case 'studyOwner':
                return text(activity.studyOwner)
            case 'affiliateName':
                return text(activity.affiliateName)
            case 'providerName':
                return text(activity.providerName)
            case 'therapeuticArea':
                return text(activity.therapeuticArea)
            case 'indication':
                return text(activity.indication, 2)
            case 'source':
                return text(activity.source || 'Atlas')
            case 'ownerRoadmapName':
                return text(activity.ownerRoadmapName)
            case 'studyNumber':
                return text(activity.studyNumber)
            case 'externalId':
                return text(activity.externalId)
            case 'countries':
                return text(toListLabel(activity.countries), 3)
            case 'compoundName':
                return text(toListLabel(activity.compoundName))
            case 'genericName':
                return text(toListLabel(activity.genericName))
            case 'budget':
                return <DataTableText text={getBudgetLabel(activity.budgetAmount)} />
            case 'funded':
                return text(toYesNo(activity.funded))
            case 'vendor':
                return text(toYesNo(activity.vendor))
            case 'corporateOrRnDGoal':
                return text(toYesNo(activity.corporateOrRnDGoal))
            case 'patientStudy':
                return text(toYesNo(activity.patientStudy))
            case 'startDate':
                return text(getDateLabel(activity.startDateValue))
            case 'endDate':
                return text(getDateLabel(activity.endDateValue))
            case 'plannedStartDate':
                return text(getDateLabel(activity.plannedStartDateValue))
            case 'plannedEndDate':
                return text(getDateLabel(activity.plannedEndDateValue))
            case 'studyReportDate':
                return text(getDateLabel(activity.studyReportDateValue))
            case 'terminationDate':
                return text(getDateLabel(activity.terminationDateValue))
            case 'resultsAvailableForExternalUseDate':
                return text(getDateLabel(activity.resultsAvailableDateValue))
            case 'lastSyncedAt':
                return text(getDateLabel(activity.lastSyncedAtValue))
            case 'createdAt':
                return text(getDateLabel(activity.createdAtValue))
            case 'updatedAt':
                return text(getDateLabel(activity.updatedAtValue))
            case 'linkedValueMessageCount':
                return linkedRecordsCount('valueMessages', activity.linkedValueMessageCount)
            case 'linkedEvidenceCount':
                return <DataTableCount value={activity.linkedEvidenceCount} />
            case 'linkedStrategicImperativeCount':
                return linkedRecordsCount('strategicImperatives', activity.linkedStrategicImperativeCount)
            case 'linkedTppCellCount':
                return <DataTableCount value={activity.linkedTppCellCount} />
            case 'deliverableCount':
                return <DataTableCount value={activity.deliverableCount} />
            case 'activityDependencyCount':
                return <DataTableCount value={activity.activityDependencyCount} />
            default:
                return null
        }
    }
